import { cn } from "../../lib/cn.js";
import { formatSignedPct } from "../../lib/format.js";
import type { OutcomePanel, OutcomeTile } from "../../lib/queries.js";

const HORIZON_LABEL: Record<number, string> = {
  5: "5 sessions",
  21: "21 sessions",
  63: "63 sessions",
};

const percent = (value: number) => `${Math.round(value * 100)}%`;

/**
 * What a signal type has been worth, one tile per forward window.
 *
 * Every tile carries its own sample size, because the number above it is
 * meaningless without one: a 100% win rate over three signals and a 60% rate
 * over twelve hundred are not the same claim, and only the second is a fact.
 * The Wilson interval is printed for the same reason - it is the part that
 * stays wide when the sample is thin.
 *
 * A horizon with nothing matured says so in words. Rendering `n: 0` as "0%"
 * would read as "this signal returns nothing", which is the opposite of "we
 * do not know yet".
 */
export function OutcomeTiles({ panel }: { panel: OutcomePanel }) {
  return (
    <ul className="grid grid-cols-1 gap-4 sm:grid-cols-3" data-testid="outcome-tiles">
      {panel.horizons.map((tile) => (
        <Tile key={tile.horizon} tile={tile} best={panel.bestEvidenced === tile.horizon} />
      ))}
    </ul>
  );
}

function Tile({ tile, best }: { tile: OutcomeTile; best: boolean }) {
  return (
    <li
      data-testid={`outcome-tile-${tile.horizon}`}
      data-best={String(best)}
      className={cn(
        "flex flex-col gap-1 border bg-surface-raised p-4",
        best ? "border-up" : "border-border",
      )}
    >
      <p className="flex items-baseline justify-between text-xs text-text-muted">
        <span>{HORIZON_LABEL[tile.horizon] ?? `${tile.horizon} sessions`}</span>
        {best ? <span className="text-up">most evidence</span> : null}
      </p>
      {tile.n === 0 ? (
        <p className="text-sm text-text-muted">Not matured yet.</p>
      ) : (
        <TileBody tile={tile} />
      )}
    </li>
  );
}

function TileBody({ tile }: { tile: OutcomeTile }) {
  return (
    <>
      <p className="text-data-lg tabular-nums">{percent(tile.winRate)}</p>
      <p className="text-xs text-text-muted tabular-nums">
        {tile.wins} of {tile.n} · {percent(tile.wilson[0])} to {percent(tile.wilson[1])}
      </p>
      <p className="flex items-baseline justify-between text-sm tabular-nums">
        <span className="text-text-muted">median, net of costs</span>
        <span className={cn(tile.paysAfterCosts ? "text-up" : "text-danger")}>
          {formatSignedPct(tile.medianNetReturn)}
        </span>
      </p>
      {tile.p25 !== null && tile.p75 !== null ? (
        <p className="text-xs text-text-muted tabular-nums">
          quartiles {formatSignedPct(tile.p25)} to {formatSignedPct(tile.p75)}
        </p>
      ) : null}
      {tile.paysAfterCosts ? null : (
        <p className="text-xs text-danger">Median does not clear costs.</p>
      )}
      {tile.insufficient ? (
        <p className="text-xs text-warning">Too few to read as a rate.</p>
      ) : null}
    </>
  );
}
